import * as THREE from 'three';
import { createASCIITexture, createASCIIMaterial } from '../utils/asciiRenderer.js';
import { CONFIG } from '../core/config.js';

export class Entity {
    constructor(x, y, char, color, scene, customSize = null) {
        this.position = new THREE.Vector3(x, 0, y);
        this.char = char;
        this.color = color;
        this.health = 100;
        this.maxHealth = 100;
        this.speed = 1;
        this.damage = 10;
        this.scale = 1;
        this.opacity = 1;
        this.rotation = 0;
        this.scene = scene;
        this.size = customSize || CONFIG.ASCII_SIZE;
        
        // Animation properties
        this.bobTime = Math.random() * Math.PI * 2;
        this.pulseTime = Math.random() * Math.PI * 2;
        this.glowIntensity = 1;
        
        // Enhanced visual properties
        this.glowRadius = 0;
        this.isPlayer = false;
        
        this.createMesh();
    }
    
    createMesh() {
        const geometry = new THREE.PlaneGeometry(this.size, this.size);
        const texture = createASCIITexture(this.char, this.color, this.isPlayer ? 256 : 128);
        const material = createASCIIMaterial(texture, this.opacity);
        
        this.mesh = new THREE.Mesh(geometry, material);
        this.mesh.position.copy(this.position);
        this.scene.add(this.mesh);
        
        // Add glow effect for player
        if (this.isPlayer) {
            this.addPlayerGlow();
        }
    }
    
    addPlayerGlow() {
        // Create a larger glowing background for the player
        const glowGeometry = new THREE.PlaneGeometry(this.size * 1.5, this.size * 1.5);
        const glowMaterial = new THREE.MeshBasicMaterial({
            color: this.color,
            transparent: true,
            opacity: 0.3,
            side: THREE.DoubleSide
        });
        
        this.glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
        this.glowMesh.position.copy(this.position);
        this.glowMesh.position.z -= 0.01; // Slightly behind main mesh
        this.scene.add(this.glowMesh);
    }
    
    update(deltaTime, camera) {
        this.updateAnimation(deltaTime);
        this.updateMesh(camera);
    }
    
    updateAnimation(deltaTime) {
        this.bobTime += deltaTime * 2;
        this.pulseTime += deltaTime * 4;
        
        this.position.y = Math.sin(this.bobTime) * 0.15;
        this.scale = 1 + Math.sin(this.pulseTime) * 0.05;
        this.rotation += deltaTime * 0.5;
        
        // Enhanced player animation
        if (this.isPlayer) {
            this.glowIntensity = 0.8 + Math.sin(this.pulseTime * 2) * 0.3;
        }
    }
    
    updateMesh(camera) {
        if (!this.mesh) return;
        
        this.mesh.position.copy(this.position);
        this.mesh.scale.setScalar(this.scale);
        this.mesh.material.opacity = this.opacity;
        this.mesh.rotation.z = this.rotation;
        
        // Update glow mesh for player
        if (this.glowMesh) {
            this.glowMesh.position.copy(this.position);
            this.glowMesh.position.z -= 0.01;
            this.glowMesh.scale.setScalar(this.scale * this.glowIntensity);
            this.glowMesh.material.opacity = 0.2 * this.glowIntensity;
        }
        
        if (camera) {
            this.mesh.lookAt(camera.position);
            if (this.glowMesh) {
                this.glowMesh.lookAt(camera.position);
            }
        }
    }
    
    takeDamage(amount, isCritical = false) {
        // Apply damage reduction if available
        if (this.damageReduction) {
            amount *= (1 - this.damageReduction);
        }
        
        this.health -= amount;
        
        // Enhanced damage feedback
        this.opacity = 0.4;
        setTimeout(() => { 
            if (this.mesh) this.opacity = 1; 
        }, 150);
        
        this.showDamageNumber(amount, isCritical);
        return this.health <= 0;
    }
    
    showDamageNumber(damage, isCritical = false) {
        const screenPos = this.getScreenPosition();
        if (!screenPos) return;
        
        const damageEl = document.createElement('div');
        damageEl.className = 'damage-number' + (isCritical ? ' critical' : '');
        damageEl.textContent = Math.floor(damage);
        damageEl.style.left = (screenPos.x + (Math.random() - 0.5) * 40) + 'px';
        damageEl.style.top = (screenPos.y + (Math.random() - 0.5) * 20) + 'px';
        damageEl.style.color = isCritical ? '#ffaa00' : '#ff6666';
        
        document.getElementById('gameContainer').appendChild(damageEl);
        
        setTimeout(() => {
            if (damageEl.parentNode) damageEl.parentNode.removeChild(damageEl);
        }, 1200);
    }
    
    getScreenPosition() {
        if (!this.mesh) return null;
        
        const vector = this.position.clone();
        vector.project(window.gameCamera); // Global camera reference
        
        const x = (vector.x * 0.5 + 0.5) * window.innerWidth;
        const y = (vector.y * -0.5 + 0.5) * window.innerHeight;
        
        return { x, y };
    }
    
    getDistanceTo(other) {
        return this.position.distanceTo(other.position);
    }
    
    getDirectionTo(other) {
        return new THREE.Vector3()
            .subVectors(other.position, this.position)
            .normalize();
    }
    
    isColliding(other, distance = 0.9) {
        return this.getDistanceTo(other) < distance;
    }
    
    destroy() {
        if (this.mesh) {
            this.scene.remove(this.mesh);
            if (this.mesh.material) {
                this.mesh.material.dispose();
            }
            if (this.mesh.geometry) {
                this.mesh.geometry.dispose();
            }
            this.mesh = null;
        }
        
        if (this.glowMesh) {
            this.scene.remove(this.glowMesh);
            if (this.glowMesh.material) {
                this.glowMesh.material.dispose();
            }
            if (this.glowMesh.geometry) {
                this.glowMesh.geometry.dispose();
            }
            this.glowMesh = null;
        }
    }
} 